import { Link } from "design-system/components/primitives/link/link";
import { CtaLink } from "design-system/components/primitives/cta-link/cta-link";
import {
  ShimmerText,
  ShimmerButton,
} from "design-system/components/primitives/shimmer-components/shimmer-components";
import { ReactNode } from "react";
import { EditAttributes } from "design-system/types/types";
import { DownloadLinkProps } from "design-system/components/primitives/article-tease/article-tease";
import { DataLayerEvent, useDataLayer } from "frontend/hooks/use-data-layer";

export interface BentoBoxDisplay {
  caseHead?: string;
  caseCategory?: DownloadLinkProps;
  caseEducatorsLink?: string;
}

export interface BentoBoxColumnProps {
  title: string;
  subtitle?: ReactNode;
  bentoItems?: Array<{
    category?: string;
    categoryLink?: string;
    title: ReactNode;
    titleLink?: string;
    description?: string | ReactNode;
    downloadLink?: DownloadLinkProps;
    display?: unknown;
    dataLayer?: DataLayerEvent;
  }>;
  isLoading?: boolean;
  viewAllLink?: string;
  viewAllText?: string;
  results?: string;
  noResultsText?: ReactNode;
  showEmpty?: boolean;
  HeadingLevel?: "h2" | "h3";
  HeadingLevelItem?: "h3" | "h4";

  editAttributes?: {
    title?: EditAttributes;
    subtitle?: EditAttributes;
    noResultsText?: EditAttributes;
    viewAll?: EditAttributes;
  };
}

export function BentoBoxColumn({
  title,
  subtitle,
  bentoItems,
  viewAllLink,
  viewAllText,
  results,
  noResultsText = (
    <p>Try adjusting your search terms to find what you are looking for.</p>
  ),
  showEmpty,
  isLoading = false,
  HeadingLevel = "h2",
  HeadingLevelItem = "h3",
  editAttributes,
}: BentoBoxColumnProps) {
  const { pushDataLayer } = useDataLayer();
  if (isLoading) {
    return (
      <div className="hbs-bento-column" data-theme="white">
        <div className="hbs-bento-column__header">
          <HeadingLevel className="hbs-bento-column__title">
            {title}
          </HeadingLevel>
          <p className="hbs-bento-column__description">{subtitle}</p>
        </div>
        <p className="hbs-global-visually-hidden">loading</p>
        <ul className="hbs-bento-items">
          {[1, 2, 3, 4, 5].map((num, i) => (
            <li className="hbs-bento-item" key={i}>
              <div className="hbs-bento-item__content">
                <ShimmerText
                  size="lg"
                  width="10"
                  className="hbs-bento-item__content--category-shimmer"
                />
                <ShimmerText rows={3} size="xs" />
              </div>
            </li>
          ))}
        </ul>
        <div className="hbs-bento-footer">
          <ShimmerButton size="md" />
          <ShimmerButton size="sm" />
        </div>
      </div>
    );
  }

  const showViewAllLink = Boolean(viewAllLink && bentoItems?.length);
  const showResults = Boolean(results && !showEmpty);

  return (
    <div className="hbs-bento-column">
      <div className="hbs-bento-column__header">
        <HeadingLevel
          {...editAttributes?.title}
          className="hbs-bento-column__title"
        >
          {title}
        </HeadingLevel>
        <p
          {...editAttributes?.subtitle}
          className="hbs-bento-column__description"
        >
          {subtitle}
        </p>
      </div>
      <ul className="hbs-bento-items">
        {bentoItems?.length ? (
          bentoItems.map((bentoItem, i) => {
            let displayItem: BentoBoxDisplay | undefined = undefined;
            if (bentoItem.display) displayItem = bentoItem.display;
            let desc = bentoItem.description;
            if (desc && typeof desc === "string") {
              const descWords = desc.split(" ");
              if (descWords.length > 18) {
                desc = `${descWords.splice(0, 18).join(" ")}…`;
              }
            }

            return (
              <li className="hbs-bento-item" key={i}>
                <div className="hbs-bento-item__content">
                  {displayItem?.caseCategory?.label ? (
                    <span className="hbs-bento-item__category">
                      {displayItem.caseCategory.label}
                    </span>
                  ) : bentoItem.category ? (
                    <span className="hbs-bento-item__category">
                      {bentoItem.categoryLink ? (
                        <Link
                          href={bentoItem.categoryLink}
                          className="hbs-bento-item__category-link"
                          onClick={() => {
                            pushDataLayer({
                              /* eslint-disable camelcase */
                              event:
                                bentoItem.dataLayer?.event ||
                                "search_result_click",
                              event_data: bentoItem.dataLayer?.event_data || {
                                search_term: "",
                                search_result_title:
                                  bentoItem.title?.toString(),
                                search_result_url: bentoItem.titleLink,
                                search_result_position: i + 1,
                                search_result_page: 1,
                              },
                              /* eslint-enable camelcase */
                            });
                          }}
                        >
                          {bentoItem.category}
                        </Link>
                      ) : (
                        bentoItem.category
                      )}
                    </span>
                  ) : null}
                  <HeadingLevelItem className="hbs-bento-item__title">
                    {bentoItem.title && typeof bentoItem.title === "string" && (
                      <Link
                        href={bentoItem.titleLink}
                        className="hbs-bento-item__title__link"
                        dangerouslySetInnerHTML={{ __html: bentoItem.title }}
                        onClick={() => {
                          pushDataLayer({
                            /* eslint-disable camelcase */
                            event:
                              bentoItem.dataLayer?.event ||
                              "search_result_click",
                            event_data: bentoItem.dataLayer?.event_data || {
                              search_term: "",
                              search_result_title: bentoItem.title?.toString(),
                              search_result_url: bentoItem.titleLink,
                              search_result_position: i + 1,
                              search_result_page: 1,
                            },
                            /* eslint-enable camelcase */
                          });
                        }}
                      />
                    )}
                    {bentoItem.title && typeof bentoItem.title !== "string" && (
                      <Link
                        href={bentoItem.titleLink}
                        className="hbs-bento-item__title__link"
                        onClick={() => {
                          pushDataLayer({
                            /* eslint-disable camelcase */
                            event:
                              bentoItem.dataLayer?.event ||
                              "search_result_click",
                            event_data: bentoItem.dataLayer?.event_data || {
                              search_term: "",
                              search_result_title: bentoItem.title?.toString(),
                              search_result_url: bentoItem.titleLink,
                              search_result_position: i + 1,
                              search_result_page: 1,
                            },
                            /* eslint-enable camelcase */
                          });
                        }}
                      >
                        {bentoItem.title}
                      </Link>
                    )}
                  </HeadingLevelItem>
                  {displayItem?.caseHead && (
                    <div className="hbs-bento-item__description">
                      {displayItem.caseHead}
                    </div>
                  )}
                  {!displayItem?.caseHead &&
                  desc &&
                  typeof desc === "string" ? (
                    <div
                      className="hbs-bento-item__description"
                      dangerouslySetInnerHTML={{ __html: desc }}
                    />
                  ) : !displayItem?.caseHead && desc ? (
                    <div className="hbs-bento-item__description">{desc}</div>
                  ) : null}

                  {bentoItem.downloadLink && (
                    <div className="hbs-bento-item__download">
                      <Link
                        className="hbs-bento-item__download-link"
                        href={bentoItem.downloadLink.link}
                        onClick={() => {
                          pushDataLayer({
                            /* eslint-disable camelcase */
                            event:
                              bentoItem.dataLayer?.event ||
                              "search_result_click",
                            event_data: {
                              search_term:
                                bentoItem.dataLayer?.event_data?.search_term ||
                                "",
                              search_result_title:
                                bentoItem.downloadLink?.label,
                              search_result_url: bentoItem.downloadLink?.link,
                              search_result_position:
                                bentoItem.dataLayer?.event_data
                                  .search_result_position || i + 1,
                              search_result_page:
                                bentoItem.dataLayer?.event_data
                                  .search_result_page || 1,
                              bento_box_category_clicked:
                                bentoItem.dataLayer?.event_data
                                  .bento_box_category_clicked,
                              search_result_tab:
                                bentoItem.dataLayer?.event_data
                                  .search_result_tab,
                            },
                            /* eslint-enable camelcase */
                          });
                        }}
                      >
                        {bentoItem.downloadLink.label}
                      </Link>
                    </div>
                  )}
                </div>
              </li>
            );
          })
        ) : (
          <li className="hbs-bento-item">
            <div className="hbs-bento-item__content">
              <div className="hbs-bento-item__description hbs-bento-item__description--no-results">
                {showEmpty ? (
                  <span>
                    Try searching for a specific term to view results from{" "}
                    {title}.
                  </span>
                ) : (
                  <>
                    <span>No results found.</span>
                    <span {...editAttributes?.noResultsText}>
                      {noResultsText}
                    </span>
                  </>
                )}
              </div>
            </div>
          </li>
        )}
      </ul>
      {showViewAllLink && showResults ? (
        <div className="hbs-bento-footer">
          {showViewAllLink && (
            <CtaLink
              editAttributes={editAttributes?.viewAll}
              href={viewAllLink}
              type="primary-button"
              isSmall
            >
              {viewAllText ?? "View all"}
            </CtaLink>
          )}
          {showResults && <p className="hbs-bento-results">{results}</p>}
        </div>
      ) : null}
    </div>
  );
}
